(function () {
  'use strict';

  const WISHLIST_KEY = 'envo_wishlist';

  function getStore() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function setStore(handles, silent) {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    if (!silent) {
      document.dispatchEvent(new CustomEvent('envo:wishlist-updated', { detail: { list: handles } }));
    }
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.Shopify?.theme?.moneyFormat || '${{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getFirstVariant(product) {
    return product.variants?.find((v) => v.available) || product.variants?.[0];
  }

  function ratingStars() {
    let html = '';
    for (let i = 0; i < 5; i++) {
      html += '<span class="wd-wishlist-card__star">★</span>';
    }
    return `<div class="wd-wishlist-card__rating" aria-hidden="true">${html}</div>`;
  }

  const WishlistPage = {
    root: null,
    loading: false,

    init() {
      this.root = document.querySelector('[data-wd-wishlist]');
      if (!this.root) return;
      this.bindEvents();
      this.render();
    },

    bindEvents() {
      document.addEventListener('click', (e) => {
        if (!this.root || !this.root.contains(e.target)) return;

        const removeBtn = e.target.closest('[data-wishlist-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.removeHandle(removeBtn.dataset.handle);
          return;
        }

        const qvBtn = e.target.closest('[data-wishlist-quick-view]');
        if (qvBtn) {
          e.preventDefault();
          if (window.EnvoCommerce?.openQuickView) {
            window.EnvoCommerce.openQuickView(qvBtn.dataset.handle);
          } else {
            window.location.href = `/products/${qvBtn.dataset.handle}`;
          }
        }
      });

      document.addEventListener('envo:wishlist-updated', () => {
        if (this.root && !this.loading) this.render();
      });
    },

    removeHandle(handle) {
      if (!handle) return;
      const list = getStore().filter((h) => h !== handle);
      setStore(list);
      if (window.EnvoCommerce) {
        window.EnvoCommerce.wishlist = list;
        window.EnvoCommerce.updateBadges();
      }
    },

    async fetchProduct(handle) {
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (!res.ok) return null;
        return res.json();
      } catch {
        return null;
      }
    },

    buildCard(product) {
      const variant = getFirstVariant(product);
      if (!variant) return '';

      const handle = escapeHtml(product.handle);
      const title = escapeHtml(product.title);
      const type = escapeHtml(product.type || '');
      const url = `/products/${handle}`;
      const sku = escapeHtml(variant.sku || '');
      const img = product.featured_image || product.images?.[0] || '';

      let priceHtml = `<span class="wd-wishlist-card__price-current">${formatMoney(variant.price)}</span>`;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml = `<span class="wd-wishlist-card__price-compare">${formatMoney(variant.compare_at_price)}</span>${priceHtml}`;
      }

      const stockHtml = product.available
        ? `<div class="wd-wishlist-card__stock is-in-stock">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2"/></svg>
            In stock
          </div>`
        : `<div class="wd-wishlist-card__stock is-out-of-stock">Out of stock</div>`;

      let actionHtml = '';
      if (!product.available) {
        actionHtml = `<button type="button" class="wd-btn wd-wishlist-card__btn" disabled>Sold out</button>`;
      } else if (product.variants.length > 1) {
        actionHtml = `<a href="${url}" class="wd-btn wd-btn--primary wd-wishlist-card__btn">Select options</a>`;
      } else {
        actionHtml = `<button type="button" class="wd-btn wd-btn--primary wd-wishlist-card__btn" data-add-to-cart data-variant-id="${variant.id}">Add to cart</button>`;
      }

      return `<article class="wd-wishlist-card" data-wishlist-row="${handle}" data-product-url="${url}">
        <div class="wd-wishlist-card__media">
          <a href="${url}" class="wd-wishlist-card__media-link" aria-label="${title}"></a>
          ${
            img
              ? `<img src="${escapeHtml(img)}" alt="${title}" width="400" height="400" loading="lazy">`
              : ''
          }
          <div class="wd-wishlist-card__actions">
            <button type="button" class="wd-wishlist-card__action-btn" data-wishlist-quick-view data-handle="${handle}" aria-label="Quick view">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
            <button type="button" class="wd-wishlist-card__action-btn is-active" data-wishlist-remove data-handle="${handle}" aria-label="Remove from wishlist">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>
        <h3 class="wd-wishlist-card__title"><a href="${url}">${title}</a></h3>
        ${type ? `<div class="wd-wishlist-card__type">${type}</div>` : ''}
        ${ratingStars()}
        ${stockHtml}
        <div class="wd-wishlist-card__price">${priceHtml}</div>
        ${actionHtml}
        ${sku ? `<div class="wd-wishlist-card__sku">SKU: ${sku}</div>` : ''}
      </article>`;
    },

    setView(state) {
      const loadingEl = this.root.querySelector('[data-wishlist-loading]');
      const emptyEl = this.root.querySelector('[data-wishlist-empty]');
      const contentEl = this.root.querySelector('[data-wishlist-content]');

      if (loadingEl) loadingEl.hidden = state !== 'loading';
      if (emptyEl) emptyEl.hidden = state !== 'empty';
      if (contentEl) contentEl.hidden = state !== 'content';
    },

    async render() {
      if (!this.root || this.loading) return;
      this.loading = true;
      this.root.closest('.wd-wishlist-page')?.classList.add('is-loading');
      this.setView('loading');

      const handles = getStore();
      if (!handles.length) {
        this.setView('empty');
        this.root.closest('.wd-wishlist-page')?.classList.remove('is-loading');
        this.loading = false;
        return;
      }

      const products = await Promise.all(handles.map((handle) => this.fetchProduct(handle)));
      const validProducts = products.filter(Boolean);
      const validHandles = validProducts.map((p) => p.handle);

      if (validHandles.length !== handles.length) {
        setStore(validHandles, true);
        if (window.EnvoCommerce) {
          window.EnvoCommerce.wishlist = validHandles;
          window.EnvoCommerce.updateBadges();
        }
      }

      if (!validProducts.length) {
        this.setView('empty');
        this.root.closest('.wd-wishlist-page')?.classList.remove('is-loading');
        this.loading = false;
        return;
      }

      const grid = this.root.querySelector('[data-wishlist-grid]');
      if (grid) {
        grid.innerHTML = validProducts.map((product) => this.buildCard(product)).join('');
      }

      this.setView('content');
      this.root.closest('.wd-wishlist-page')?.classList.remove('is-loading');
      this.loading = false;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => WishlistPage.init());
  } else {
    WishlistPage.init();
  }
})();
